import { defineComponent as _defineComponent } from 'vue'
import { store } from '@/store';
import VueProgrammaticInvisibleGoogleRecaptcha from 'vue-programmatic-invisible-google-recaptcha';
import { useTranslations } from '@/core/translate.mixin';
import linetagService from '@/services/linetag.service';
import { computed, onMounted, ref, useSlots, watch } from 'vue';
import { useValidator } from '@/core/app.service';
import { useNewsletter } from '@/store/newsletter.composable';

interface INewsletterProfessionItem {
    name?: string;
    id?: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'NewNewsletterForm',
  props: {
    lateRedirectPageLink: { default: '' },
    isModalMode: { type: Boolean },
    contentId: { default: undefined },
    channelOverride: null
  },
  emits: ["onSubmit"],
  setup(__props: any, { emit }: { emit: ({(e: 'onSubmit'): void }), expose: any, slots: any, attrs: any }) {

const props = __props as { lateRedirectPageLink: string, isModalMode?: boolean, contentId: string, channelOverride?: string }

const recaptchaKey = computed<string>(() => store.getters['app/recaptchaKey']);
const selectedMode = computed<IMode>(() => store.getters['app/selectedMode']);
const selectedLanguage = computed<MarketViewObject>(() => store.getters['app/selectedLanguage']);
const isPrivateMode = computed<boolean>(() => store.getters['app/isPrivateMode']);
const theme = computed<ColorTheme>(() => store.getters['app/theme']);
const textColor = computed<OpacityFunction>(() => store.getters['app/textColor']);

const { getFormOptions, privacyPolicyStatementHtml, newsletterFlagText, profilingFlagText, submitFormOptions, getPrefilledNewsletterSignupInformation } = useNewsletter();





const formDataPending = ref<boolean>(true);
const submittingFormData = ref<boolean>(false);
const formSubmitted = ref<boolean>(false);
const newsletterForm = ref<SubscribeToNewsletterWebRequest>({
    newsletterFlagAccepted: false,
    profilingFlagAccepted: false,
    interestedInIndoor: false,
    interestedInOutdoor: false,
    userType: '',
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    profession: '',
    company: '',
    recaptchaToken: '',
    contentId: '',
    channelOverride: ''
});

const newsletterFormData = ref<NewsletterSignupFormViewModel>({
    userTypes: [],
    countries: [],
    professions: []
});

const selectedProfession = ref<INewsletterProfessionItem>({});
const _uid = Math.floor(Math.random() * 1000000).toString();
const componentId = 'newsletterFormRecaptcha';
const labelNamespace = 'Pages.NewsletterSignup.NewsletterForm';

const $validator = useValidator();
const { t } = useTranslations();
onMounted(() => {
    const dictionary = {
        custom: {
            email: {
                required: t(`${labelNamespace}.EmailRequiredErrorMessage`),
                email: t(`${labelNamespace}.EmailInvalidErrorMessage`)
            },
            firstName: {
                required: t(`${labelNamespace}.FirstNameRequiredErrorMessage`)
            },
            lastName: {
                required: t(`${labelNamespace}.LastNameRequiredErrorMessage`)
            },
            company: {
                required: t(`${labelNamespace}.CompanyRequiredErrorMessage`)
            },
            newsletterFlagAccepted: {
                required: t(`${labelNamespace}.NewsletterFlagRequiredErrorMessage`)
            },
            profilingFlagAccepted: {
                required: t(`${labelNamespace}.ProfilingFlagRequiredErrorMessage`)
            }
        }
    };
    $validator.localize('en', dictionary);
    applyPrefilledNewsletterSignupForm(prefilledNewsletterSignupInformation.value);

    getFormOptions().then((data: NewsletterSignupFormViewModel) => {
        newsletterFormData.value = data;
        formDataPending.value = false;
    });
    newsletterForm.value.userType = selectedMode.value.name === 'private' ? 'Private' : 'Professional';
    newsletterForm.value.country = selectedLanguage.value.name;
});

function validateAndSubmitRequest() {
    $validator
        .validateAll()
        .then(isValid => {
            if (isValid) {
                const recaptchaComponent = refs[componentId + _uid].value as any;
                recaptchaComponent.execute();

                if (props.lateRedirectPageLink !== '') {
                    setTimeout(() => {
                        redirectToPage();
                    }, 15000);
                }
            } else {
                scrollInvalidElementIntoView();
            }
        })
        .catch(x => console.log('catch' + x));
}

const prefilledNewsletterSignupInformation = computed<IPrefilledNewsletterSignupRequestForm>(() => getPrefilledNewsletterSignupInformation());
watch(prefilledNewsletterSignupInformation, applyPrefilledNewsletterSignupForm, { deep: true });

function applyPrefilledNewsletterSignupForm(value: IPrefilledNewsletterSignupRequestForm) {
    if (value) {
        newsletterForm.value.firstName = value.firstName;
        newsletterForm.value.lastName = value.lastName;
        newsletterForm.value.email = value.emailAddress;
    }
}

const refs = { [componentId + _uid]: ref(null) };

function setRecaptchaRef(el: any) {
    setTimeout(() => {
        refs[componentId + _uid].value = el;
    }, 100);
}

function submitForm(recaptchaToken) {
    submittingFormData.value = true;
    newsletterForm.value.channelOverride = props.channelOverride;
    newsletterForm.value.recaptchaToken = recaptchaToken;
    newsletterForm.value.contentId = props.contentId;
    submitFormOptions(newsletterForm.value)
        .then(
            () => {
                handleSuccess();
            },
            () => {
                submittingFormData.value = false;
            });
}

function redirectToPage() {
    document.location.href = props.lateRedirectPageLink;
}

const slots = useSlots();

function handleSuccess() {
    formSubmitted.value = true;
    emit('onSubmit');
    submittingFormData.value = false;
    if (slots['success-message']) {
        scrollToElement('newsletter-signup-success');
    }
    linetagService.trackCompleteRegistration();
}

function scrollInvalidElementIntoView() {
    const firstInvalidElement = document.querySelector('.is-danger');
    if (firstInvalidElement) {
        firstInvalidElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
}

function setSelectedProfession() {
    newsletterForm.value.profession = isPrivateMode.value ? '' : selectedProfession.value.id;
}

function scrollToElement(elementId: string): void {
    let element: HTMLElement;
    element = document.getElementById(elementId);

    if (!element) {
        setTimeout(() => {
            element = document.getElementById(elementId);
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }, 1500);
    } else {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
}

return { __sfc: true,recaptchaKey, selectedMode, selectedLanguage, isPrivateMode, theme, textColor, getFormOptions, privacyPolicyStatementHtml, newsletterFlagText, profilingFlagText, submitFormOptions, getPrefilledNewsletterSignupInformation, props, emit, formDataPending, submittingFormData, formSubmitted, newsletterForm, newsletterFormData, selectedProfession, _uid, componentId, labelNamespace, $validator, t, validateAndSubmitRequest, prefilledNewsletterSignupInformation, applyPrefilledNewsletterSignupForm, refs, setRecaptchaRef, submitForm, redirectToPage, slots, handleSuccess, scrollInvalidElementIntoView, setSelectedProfession, scrollToElement, VueProgrammaticInvisibleGoogleRecaptcha }
}

})